import React, { useState, useRef, useEffect } from 'react';
import { COVER_FONT_CATEGORIES } from '../types';

interface FontPickerProps {
  value: string;
  onChange: (font: string) => void;
}

const ALL_ID = 'all';

const FontPicker: React.FC<FontPickerProps> = ({ value, onChange }) => {
  const [open, setOpen]         = useState(false);
  const [activeTab, setActiveTab] = useState(ALL_ID);
  const containerRef            = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const visibleFonts = activeTab === ALL_ID
    ? COVER_FONT_CATEGORIES.flatMap(c => c.fonts)
    : (COVER_FONT_CATEGORIES.find(c => c.id === activeTab)?.fonts ?? []);

  const selectFont = (family: string) => {
    // Pre-warm the font so Konva canvas renders it immediately
    document.fonts.load(`700 18px "${family}"`).finally(() => {
      onChange(family);
      setOpen(false);
    });
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500 hover:bg-white transition-colors"
      >
        <span style={{ fontFamily: `"${value}", serif` }} className="truncate text-gray-900">
          {value}
        </span>
        <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 flex flex-col"
          style={{ maxHeight: 320 }}>
          {/* Category tabs */}
          <div className="flex gap-0.5 px-2 pt-2 pb-1 border-b border-gray-100 flex-wrap">
            <button
              onClick={() => setActiveTab(ALL_ID)}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                activeTab === ALL_ID ? 'bg-orange-500 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              All
            </button>
            {COVER_FONT_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                  activeTab === cat.id ? 'bg-orange-500 text-white' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Font list */}
          <div className="overflow-y-auto flex-1">
            {visibleFonts.map(({ family }) => (
              <button
                key={family}
                type="button"
                onClick={() => selectFont(family)}
                className={`w-full text-left px-3 py-2.5 text-base transition-colors border-b border-gray-50 last:border-0 ${
                  value === family
                    ? 'bg-orange-50 text-orange-700'
                    : 'text-gray-800 hover:bg-gray-50'
                }`}
                style={{ fontFamily: `"${family}", serif` }}
              >
                {family}
                {value === family && (
                  <span className="float-right text-orange-400 text-xs mt-1">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FontPicker;
